import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  BoxProps,
  CheckboxProps,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabelProps,
  Grid,
  GridProps,
  IconButton,
  MenuItem,
  Pagination,
  PaginationItem,
  PaginationProps,
  Select,
  SelectChangeEvent,
  Stack,
  StackProps,
  SvgIcon,
  TextField,
  Typography
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'
import { useLenis } from 'lenis/react'
import { CalendarDaysIcon, SlidersHorizontalIcon, XIcon } from 'lucide-react'
import { useRouter } from 'next/router'
import { useShallow } from 'zustand/shallow'

import Button from '@/components/base/button'
import { SearchCard, SearchCard as SearchCardProps } from '@/components/sections/search-grid'
import Spacer from '@/components/sections/spacer'
import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks'
import { useLayoutStore } from '@/store/layout'
import { useLocaleStore } from '@/store/locale'
import { useWishlistStore } from '@/store/wishlist'
import { formatPrice } from '@/utils/strings'
import { ADVERTISEMENT_SEARCH_PER_PAGE } from '@/constants/pagination'

import 'dayjs/locale/it'

import MapSearchModal from './map-search-modal'
import { useAdvertisementsSearch } from '@/api/advertisements'
import { Advertisement, AdvertisementSearchParams } from '@/api/advertisements/types'
import { useCities } from '@/api/cities'
import { useFeatures } from '@/api/features'
import { FeatureGroup } from '@/api/features/types'
import { City } from '@/api/types'
import { WPImage, WPLink, WPPageData } from '@/types'

/**
 * Types
 */
export type SearchProps = {
  pageProps?: Omit<WPPageData, 'acf'> & {
    acf: {
      title?: string
      results?: SearchCardProps[]
    }
  }
}

type OrderByValue = 'price' | '-price' | 'date' | '-date' | ''

type SearchFilters = {
  q: string
  city_id: number | undefined
  features: string[]
  min_price: number | undefined
  max_price: number | undefined
  min_deposit: number | undefined
  max_deposit: number | undefined
  min_area: number | undefined
  max_area: number | undefined
  min_bedrooms: number | undefined
  min_bathrooms: number | undefined
  min_floor: number | undefined
  max_floor: number | undefined
  min_contract_months: number | undefined
  max_contract_months: number | undefined
  min_available_places: number | undefined
  available_from: string | undefined
  available_to: string | undefined
  order_by: OrderByValue
  page: number
}

/**
 * Helper: Parse URL query parameter to number
 */
const parseIntParam = (param: string | string[] | undefined): number | undefined => {
  if (!param) return undefined
  const value = Array.isArray(param) ? param[0] : param
  const parsed = parseInt(value, 10)
  return isNaN(parsed) ? undefined : parsed
}

/**
 * Helper: Parse URL query parameter to string array
 */
const parseArrayParam = (param: string | string[] | undefined): string[] => {
  if (!param) return []

  return (Array.isArray(param) ? param : [param])
    .map((item) => String(item).trim())
    .filter((item) => item.length > 0)
}

/**
 * Hook: useSearchFilters
 * Gestisce la sincronizzazione tra stato locale e URL
 */
const useSearchFilters = () => {
  const router = useRouter()

  // Parse URL params into filters object
  const getFiltersFromUrl = useCallback((): SearchFilters => {
    const q = router.query
    return {
      q: (q.q as string) || '',
      city_id: parseIntParam(q.city_id),
      features: parseArrayParam(q['features[]'] as string | string[] | undefined),
      min_price: parseIntParam(q.min_price),
      max_price: parseIntParam(q.max_price),
      min_deposit: parseIntParam(q.min_deposit),
      max_deposit: parseIntParam(q.max_deposit),
      min_area: parseIntParam(q.min_area),
      max_area: parseIntParam(q.max_area),
      min_bedrooms: parseIntParam(q.min_bedrooms),
      min_bathrooms: parseIntParam(q.min_bathrooms),
      min_floor: parseIntParam(q.min_floor),
      max_floor: parseIntParam(q.max_floor),
      min_contract_months: parseIntParam(q.min_contract_months),
      max_contract_months: parseIntParam(q.max_contract_months),
      min_available_places: parseIntParam(q.min_available_places),
      available_from: (q.available_from as string) || undefined,
      available_to: (q.available_to as string) || undefined,
      order_by: (q.order_by as OrderByValue) || '',
      page: parseIntParam(q.page) || 1
    }
  }, [router.query])

  // Stato locale dei filtri
  const [filters, setFilters] = useState<SearchFilters>(getFiltersFromUrl)

  // Ref per accedere ai filtri correnti senza causare ricreazione dei callback
  const filtersRef = useRef(filters)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  // Sincronizza stato quando cambia la URL (navigazione back/forward)
  useEffect(() => {
    setFilters(getFiltersFromUrl())
  }, [getFiltersFromUrl])

  // Parametri per la API (il debounce di q è gestito da SearchInput)
  const searchParams = useMemo(
    () => ({
      q: filters.q || undefined,
      city_id: filters.city_id,
      features: filters.features.length > 0 ? filters.features : undefined,
      min_price: filters.min_price,
      max_price: filters.max_price,
      min_deposit: filters.min_deposit,
      max_deposit: filters.max_deposit,
      min_area: filters.min_area,
      max_area: filters.max_area,
      min_bedrooms: filters.min_bedrooms,
      min_bathrooms: filters.min_bathrooms,
      min_floor: filters.min_floor,
      max_floor: filters.max_floor,
      min_contract_months: filters.min_contract_months,
      max_contract_months: filters.max_contract_months,
      min_available_places: filters.min_available_places,
      available_from: filters.available_from,
      available_to: filters.available_to,
      // Default to most recent first when user hasn't selected an order
      order_by: filters.order_by || '-date',
      page: filters.page,
      per_page: ADVERTISEMENT_SEARCH_PER_PAGE
    }),
    [filters]
  )

  // Aggiorna URL con i nuovi filtri (usa ref per evitare ricreazioni)
  const updateUrl = useCallback(
    (newFilters: Partial<SearchFilters>) => {
      const updated = { ...filtersRef.current, ...newFilters }
      const query: Record<string, string | string[]> = {}

      // Solo aggiunge alla query i valori definiti
      if (updated.q) query.q = updated.q
      if (updated.city_id !== undefined) query.city_id = String(updated.city_id)
      if (updated.features.length > 0) query['features[]'] = updated.features
      if (updated.min_price !== undefined) query.min_price = String(updated.min_price)
      if (updated.max_price !== undefined) query.max_price = String(updated.max_price)
      if (updated.min_deposit !== undefined) query.min_deposit = String(updated.min_deposit)
      if (updated.max_deposit !== undefined) query.max_deposit = String(updated.max_deposit)
      if (updated.min_area !== undefined) query.min_area = String(updated.min_area)
      if (updated.max_area !== undefined) query.max_area = String(updated.max_area)
      if (updated.min_bedrooms !== undefined) query.min_bedrooms = String(updated.min_bedrooms)
      if (updated.min_bathrooms !== undefined) query.min_bathrooms = String(updated.min_bathrooms)
      if (updated.min_floor !== undefined) query.min_floor = String(updated.min_floor)
      if (updated.max_floor !== undefined) query.max_floor = String(updated.max_floor)
      if (updated.min_contract_months !== undefined)
        query.min_contract_months = String(updated.min_contract_months)
      if (updated.max_contract_months !== undefined)
        query.max_contract_months = String(updated.max_contract_months)
      if (updated.min_available_places !== undefined)
        query.min_available_places = String(updated.min_available_places)
      if (updated.available_from) query.available_from = updated.available_from
      if (updated.available_to) query.available_to = updated.available_to
      if (updated.order_by) query.order_by = updated.order_by
      if (updated.page > 1) query.page = String(updated.page)

      // Use asPath to get the actual URL path instead of the route pattern ([...routes])
      const pathname = router.asPath.split('?')[0]
      router.push({ pathname, query }, undefined, { shallow: true })
    },
    [router]
  )

  // Handler generico per aggiornare un singolo filtro
  const updateFilter = useCallback(
    <K extends keyof SearchFilters>(key: K, value: SearchFilters[K], resetPage = true) => {
      const updates: Partial<SearchFilters> = { [key]: value }
      if (resetPage && key !== 'page') updates.page = 1
      setFilters((prev) => ({ ...prev, ...updates }))
      updateUrl(updates)
    },
    [updateUrl]
  )

  // Handlers specifici per ogni filtro
  const setQ = useCallback((value: string) => updateFilter('q', value), [updateFilter])
  const setCityId = useCallback(
    (value: number | undefined) => updateFilter('city_id', value),
    [updateFilter]
  )
  const setMinPrice = useCallback(
    (value: number | undefined) => updateFilter('min_price', value),
    [updateFilter]
  )
  const setMaxPrice = useCallback(
    (value: number | undefined) => updateFilter('max_price', value),
    [updateFilter]
  )
  const setMinDeposit = useCallback(
    (value: number | undefined) => updateFilter('min_deposit', value),
    [updateFilter]
  )
  const setMaxDeposit = useCallback(
    (value: number | undefined) => updateFilter('max_deposit', value),
    [updateFilter]
  )
  const setMinArea = useCallback(
    (value: number | undefined) => updateFilter('min_area', value),
    [updateFilter]
  )
  const setMaxArea = useCallback(
    (value: number | undefined) => updateFilter('max_area', value),
    [updateFilter]
  )
  const setMinBedrooms = useCallback(
    (value: number | undefined) => updateFilter('min_bedrooms', value),
    [updateFilter]
  )
  const setMinBathrooms = useCallback(
    (value: number | undefined) => updateFilter('min_bathrooms', value),
    [updateFilter]
  )
  const setMinFloor = useCallback(
    (value: number | undefined) => updateFilter('min_floor', value),
    [updateFilter]
  )
  const setMaxFloor = useCallback(
    (value: number | undefined) => updateFilter('max_floor', value),
    [updateFilter]
  )
  const setMinContractMonths = useCallback(
    (value: number | undefined) => updateFilter('min_contract_months', value),
    [updateFilter]
  )
  const setMaxContractMonths = useCallback(
    (value: number | undefined) => updateFilter('max_contract_months', value),
    [updateFilter]
  )
  const setMinAvailablePlaces = useCallback(
    (value: number | undefined) => updateFilter('min_available_places', value),
    [updateFilter]
  )
  const setAvailableFrom = useCallback(
    (value: string | undefined) => updateFilter('available_from', value),
    [updateFilter]
  )
  const setAvailableTo = useCallback(
    (value: string | undefined) => updateFilter('available_to', value),
    [updateFilter]
  )
  const setOrderBy = useCallback(
    (value: OrderByValue) => updateFilter('order_by', value),
    [updateFilter]
  )
  const setPage = useCallback((value: number) => updateFilter('page', value, false), [updateFilter])

  const toggleFeature = useCallback(
    (featureSlug: string, checked: boolean) => {
      const newFeatures = checked
        ? [...filters.features, featureSlug]
        : filters.features.filter((slug) => slug !== featureSlug)

      updateFilter('features', newFeatures)
    },
    [filters.features, updateFilter]
  )

  // Handler per select esclusivo: sostituisce tutte le feature del gruppo con la nuova selezione
  const setFeatureGroup = useCallback(
    (groupFeatures: string[], selectedSlug: string | null) => {
      // Rimuovi tutte le feature del gruppo
      const featuresWithoutGroup = filters.features.filter((slug) => !groupFeatures.includes(slug))
      // Aggiungi la nuova feature se selezionata
      const newFeatures = selectedSlug
        ? [...featuresWithoutGroup, selectedSlug]
        : featuresWithoutGroup

      updateFilter('features', newFeatures)
    },
    [filters.features, updateFilter]
  )

  return {
    filters,
    searchParams,
    setQ,
    setCityId,
    setMinPrice,
    setMaxPrice,
    setMinDeposit,
    setMaxDeposit,
    setMinArea,
    setMaxArea,
    setMinBedrooms,
    setMinBathrooms,
    setMinFloor,
    setMaxFloor,
    setMinContractMonths,
    setMaxContractMonths,
    setMinAvailablePlaces,
    setAvailableFrom,
    setAvailableTo,
    toggleFeature,
    setFeatureGroup,
    setOrderBy,
    setPage
  }
}

/**
 * Component: SearchPagination
 */
export const SearchPagination = (props: PaginationProps) => {
  return (
    <Pagination
      renderItem={(item) => (
        <PaginationItem
          slots={{
            previous: () => (
              <SvgIcon>
                <use href="#ico-arrow-prev" />
              </SvgIcon>
            ),
            next: () => (
              <SvgIcon>
                <use href="#ico-arrow-next" />
              </SvgIcon>
            )
          }}
          {...item}
        />
      )}
      {...props}
    />
  )
}

/**
 * Component: FilterCheckbox
 */
type FilterCheckboxProps = BoxProps & {
  checked?: boolean
  onChange: CheckboxProps['onChange']
}

const FilterCheckbox = ({ sx, onChange, checked, ...props }: FilterCheckboxProps) => {
  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: '1.6rem',
        height: '1.6rem',
        ...sx
      }}
      {...props}
    >
      <Box
        component="input"
        type="checkbox"
        checked={checked}
        onChange={onChange}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          margin: 0,
          padding: 0,
          opacity: 0,
          '&:checked+*:after': {
            transform: 'translate(-50%, -50%) scale(1)'
          }
        }}
      />

      <Box
        component="span"
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: (theme) => theme.shape.borderRadius1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          '&:after': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '1rem',
            height: '1rem',
            transform: 'translate(-50%, -50%) scale(0)',
            borderRadius: '2px',
            backgroundColor: (theme) => theme.palette.primary.main
          }
        }}
      />
    </Box>
  )
}

/**
 * Component: ControlLabel
 */
type ControlLabelProps = StackProps & {
  label: FormControlLabelProps['label']
  control: FormControlLabelProps['control']
}

const ControlLabel = ({ sx, label, control, ...props }: ControlLabelProps) => {
  return (
    <Stack
      component="label"
      direction="row"
      alignItems="center"
      sx={{ p: 1, m: -1, cursor: 'pointer', display: 'inline-flex', ...sx }}
      {...props}
    >
      {control}

      <Typography
        component="span"
        variant="textRegular"
        color="text.secondaryContrastText"
        sx={{ ml: 2 }}
      >
        {label}
      </Typography>
    </Stack>
  )
}

/**
 * Component: PriceRangeInput
 */
const PRICE_OPTIONS = [
  { value: '', label: '' },
  { value: '300', label: '€300' },
  { value: '400', label: '€400' },
  { value: '500', label: '€500' },
  { value: '600', label: '€600' },
  { value: '700', label: '€700' },
  { value: '800', label: '€800' },
  { value: '900', label: '€900' },
  { value: '1000', label: '€1000+' }
]

type PriceRangeInputProps = {
  minPrice: number | undefined
  maxPrice: number | undefined
  onMinChange: (_value: number | undefined) => void
  onMaxChange: (_value: number | undefined) => void
}

const PriceRangeInput = ({
  minPrice,
  maxPrice,
  onMinChange,
  onMaxChange
}: PriceRangeInputProps) => {
  const { strings } = useAppStore()
  const handleMinChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value ? parseInt(event.target.value, 10) : undefined
      onMinChange(value)
    },
    [onMinChange]
  )

  const handleMaxChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value ? parseInt(event.target.value, 10) : undefined
      onMaxChange(value)
    },
    [onMaxChange]
  )

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Typography
        variant="textRegular"
        fontWeight="500"
        color="text.secondaryContrastText"
        sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
      >
        {strings?.labels?.price_range}
      </Typography>
      <Stack direction="row" spacing={4} alignItems="center">
        <FormControl size="small" sx={{ flex: 1 }}>
          <Select
            value={minPrice?.toString() ?? ''}
            onChange={handleMinChange}
            variant="standard"
            displayEmpty
            sx={{
              borderRadius: (theme) => theme.shape.borderRadius2,
              '& .MuiSelect-select': {
                py: 1
              }
            }}
          >
            {PRICE_OPTIONS.map((option) => (
              <MenuItem key={`min-${option.value}`} value={option.value}>
                {option.value ? option.label : strings?.labels?.any}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: 1 }}>
          <Select
            value={maxPrice?.toString() ?? ''}
            onChange={handleMaxChange}
            variant="standard"
            displayEmpty
            sx={{
              borderRadius: (theme) => theme.shape.borderRadius2,
              '& .MuiSelect-select': {
                py: 1
              }
            }}
          >
            {PRICE_OPTIONS.map((option) => (
              <MenuItem key={`max-${option.value}`} value={option.value}>
                {option.value ? option.label : strings?.labels?.any}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Box>
  )
}

/**
 * Component: AreaRangeInput
 */
const AREA_OPTIONS = [
  { value: '', label: '' },
  { value: '20', label: '20 m²' },
  { value: '30', label: '30 m²' },
  { value: '40', label: '40 m²' },
  { value: '50', label: '50 m²' },
  { value: '60', label: '60 m²' },
  { value: '70', label: '70 m²' },
  { value: '80', label: '80 m²' },
  { value: '90', label: '90 m²' },
  { value: '100', label: '100+ m²' }
]

type AreaRangeInputProps = {
  minArea: number | undefined
  maxArea: number | undefined
  onMinChange: (_value: number | undefined) => void
  onMaxChange: (_value: number | undefined) => void
}

const AreaRangeInput = ({ minArea, maxArea, onMinChange, onMaxChange }: AreaRangeInputProps) => {
  const { strings } = useAppStore()
  const handleMinChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value ? parseInt(event.target.value, 10) : undefined
      onMinChange(value)
    },
    [onMinChange]
  )

  const handleMaxChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value ? parseInt(event.target.value, 10) : undefined
      onMaxChange(value)
    },
    [onMaxChange]
  )

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Typography
        variant="textRegular"
        fontWeight="500"
        color="text.secondaryContrastText"
        sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
      >
        {strings?.labels?.area_range}
      </Typography>
      <Stack direction="row" spacing={4} alignItems="center">
        <FormControl size="small" sx={{ flex: 1 }}>
          <Select
            value={minArea?.toString() ?? ''}
            onChange={handleMinChange}
            variant="standard"
            displayEmpty
            sx={{
              borderRadius: (theme) => theme.shape.borderRadius2,
              '& .MuiSelect-select': {
                py: 1
              }
            }}
          >
            {AREA_OPTIONS.map((option) => (
              <MenuItem key={`min-area-${option.value}`} value={option.value}>
                {option.value ? option.label : strings?.labels?.any}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: 1 }}>
          <Select
            value={maxArea?.toString() ?? ''}
            onChange={handleMaxChange}
            variant="standard"
            displayEmpty
            sx={{
              borderRadius: (theme) => theme.shape.borderRadius2,
              '& .MuiSelect-select': {
                py: 1
              }
            }}
          >
            {AREA_OPTIONS.map((option) => (
              <MenuItem key={`max-area-${option.value}`} value={option.value}>
                {option.value ? option.label : strings?.labels?.any}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Box>
  )
}

/**
 * Component: FeatureFilterGroup
 */
type FeatureFilterGroupProps = {
  groupKey: string
  group: FeatureGroup
  selectedFeatures: string[]
  onToggle: (_slug: string, _checked: boolean) => void
  onGroupChange?: (_groupSlugs: string[], _selectedSlug: string | null) => void
}

const FeatureFilterGroup = ({
  groupKey,
  group,
  selectedFeatures,
  onToggle,
  onGroupChange
}: FeatureFilterGroupProps) => {
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const { strings } = useAppStore()
  const isUngrouped = group.slug === 'ungrouped'

  // Handler per select esclusivo (single) - sostituisce atomicamente le feature del gruppo
  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newValue = event.target.value
      const groupSlugs = group.features.map((f) => f.slug).filter((slug): slug is string => !!slug)

      // Usa il handler dedicato per la sostituzione atomica
      if (onGroupChange) {
        onGroupChange(groupSlugs, newValue || null)
      }
    },
    [group.features, onGroupChange]
  )

  // Se è il gruppo ungrouped, mostra checkbox
  if (isUngrouped) {
    return (
      <Box key={groupKey} sx={{ display: 'grid', gap: 2 }}>
        <Typography
          variant="textRegular"
          fontWeight="500"
          color="text.secondaryContrastText"
          sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
        >
          {group.name?.[currentLocale!] || strings?.labels?.features}
        </Typography>

        {group.features.map((feature) => {
          const featureSlug = feature.slug
          if (!featureSlug) return null

          return (
            <ControlLabel
              key={featureSlug}
              control={
                <FilterCheckbox
                  checked={selectedFeatures.includes(featureSlug)}
                  onChange={(event) =>
                    onToggle(featureSlug, (event.target as HTMLInputElement).checked)
                  }
                />
              }
              label={feature.name?.[currentLocale!]}
            />
          )
        })}
      </Box>
    )
  }

  // Altrimenti mostra select esclusivo (single)
  const groupSlugs = group.features.map((f) => f.slug).filter((slug): slug is string => !!slug)
  const selectedInGroup = groupSlugs.find((slug) => selectedFeatures.includes(slug)) || ''

  return (
    <Box key={groupKey} sx={{ display: 'grid', gap: 2 }}>
      <Typography
        variant="textRegular"
        fontWeight="500"
        color="text.secondaryContrastText"
        sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
      >
        {group.name?.[currentLocale!] || 'Caratteristiche'}
      </Typography>

      <FormControl size="small">
        <Select
          value={selectedInGroup}
          onChange={handleSelectChange}
          variant="standard"
          displayEmpty
          renderValue={(selected) => {
            if (!selected) return `${strings?.labels?.select}...`
            const feature = group.features.find((f) => f.slug === selected)
            return feature?.name?.it || selected
          }}
          sx={{
            borderRadius: (theme) => theme.shape.borderRadius2,
            '& .MuiSelect-select': {
              py: 1
            }
          }}
        >
          <MenuItem value="">
            <em>{strings?.labels?.any}</em>
          </MenuItem>
          {group.features.map((feature) => {
            const featureSlug = feature.slug
            if (!featureSlug) return null

            return (
              <MenuItem key={featureSlug} value={featureSlug}>
                {feature.name?.[currentLocale!] || featureSlug}
              </MenuItem>
            )
          })}
        </Select>
      </FormControl>
    </Box>
  )
}

/**
 * Component: SearchResults
 */
type SearchResultsProps = {
  isLoading: boolean
  collection: Advertisement[] | undefined
}

const SearchResults = ({ isLoading, collection }: SearchResultsProps) => {
  const { t } = useTranslation('common')
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const { items: wishlistItems, toggleItem } = useWishlistStore()
  const { strings } = useAppStore()

  const handleWishlistToggle = useCallback(
    (id: number) => {
      const model = collection?.find((m) => m.id === id)
      if (model) {
        toggleItem({
          id: model.id,
          title: model.title?.[currentLocale!] || ''
        })
      }
    },
    [collection, currentLocale, toggleItem]
  )

  if (isLoading) {
    return (
      <Grid size={{ xs: 4, sm: 8, md: 8, lg: 9 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="textLarge" color="text.secondary">
            {strings?.labels?.loading_advertisements}
          </Typography>
        </Box>
      </Grid>
    )
  }

  if (!collection?.length) {
    return (
      <Grid size={{ xs: 4, sm: 8, md: 8, lg: 9 }}>
        <Typography variant="textLarge" color="text.secondary" sx={{ py: 4 }}>
          {strings?.labels?.no_advertisements_found}
        </Typography>
      </Grid>
    )
  }

  return (
    <>
      {collection.map((model) => {
        return (
          <SearchCard
            key={model.id}
            size={{ xs: 4, md: 4, lg: 3 }}
            skeleton={false}
            pretitle={model.property.city?.name?.[currentLocale!] || ''}
            title={model.title?.[currentLocale!] || ''}
            subtitle={`${formatPrice((Number(model.price) || 0) + (Number(model.included_expenses) || 0))}/${strings?.labels?.month}`}
            text={model.description?.[currentLocale!] || ''}
            badge={
              isHappa && model.boost_valid_until
                ? { label: t('advertisement.featured') }
                : undefined
            }
            chips={[...(model.features || []), ...(model.property?.features || [])]
              .filter((f) => f.group?.slug === 'struct_type' || f.group?.slug === 'accessible')
              .sort((a, b) => {
                const order = ['struct_type', 'accessible']
                return order.indexOf(a.group?.slug || '') - order.indexOf(b.group?.slug || '')
              })
              ?.map((f) => ({ label: f.name?.[currentLocale!] || '' }))}
            image={
              model.attachments?.[0]
                ? ({
                    url: `/images/${model.attachments[0].path}`,
                    alt: model.title?.[currentLocale!] || ''
                  } as WPImage)
                : undefined
            }
            link={
              {
                url: `/advertisements/${model.id}`,
                title: model.title?.[currentLocale!] || '',
                target: ''
              } as WPLink
            }
            wishlist={{
              advertisementId: model.id,
              isInWishlist: wishlistItems.some((item) => item.id === model.id),
              onToggle: handleWishlistToggle
            }}
          />
        )
      })}
    </>
  )
}

/**
 * Component: SearchHeader
 */
type SearchHeaderProps = {
  title: string
  onOpenFilters?: () => void
  onOpenMap?: () => void
}

const SearchHeader = ({ title, onOpenFilters, onOpenMap }: SearchHeaderProps) => {
  const { strings } = useAppStore()
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'flex-start', sm: 'flex-end', md: 'center' }}
      justifyContent="space-between"
      gap={2}
    >
      <Typography
        variant="h2"
        color={isHappa ? 'primary' : 'text.secondaryContrastText'}
        component="h1"
        fontWeight={isHappa ? '700' : '500'}
      >
        {title}
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton
          size="medium"
          color="secondary"
          onClick={onOpenFilters}
          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
        >
          <SlidersHorizontalIcon />
        </IconButton>
        <Button
          variant="contained"
          color="info"
          icon="pin"
          onClick={onOpenMap}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {strings?.labels?.view_on_map}
        </Button>
      </Stack>
    </Stack>
  )
}

/**
 * Component: SearchFilters
 */
type SearchFiltersProps = {
  q: string
  cityId: number | undefined
  minPrice: number | undefined
  maxPrice: number | undefined
  minDeposit: number | undefined
  maxDeposit: number | undefined
  minArea: number | undefined
  maxArea: number | undefined
  minBedrooms: number | undefined
  minBathrooms: number | undefined
  minFloor: number | undefined
  maxFloor: number | undefined
  minContractMonths: number | undefined
  maxContractMonths: number | undefined
  minAvailablePlaces: number | undefined
  availableFrom: string | undefined
  availableTo: string | undefined
  selectedFeatures: string[]
  orderBy: OrderByValue
  isFeaturesLoading: boolean
  features: Record<string, FeatureGroup> | undefined
  onQChange: (_value: string) => void
  onMinPriceChange: (_value: number | undefined) => void
  onMaxPriceChange: (_value: number | undefined) => void
  onMinDepositChange: (_value: number | undefined) => void
  onMaxDepositChange: (_value: number | undefined) => void
  onMinAreaChange: (_value: number | undefined) => void
  onMaxAreaChange: (_value: number | undefined) => void
  onMinBedroomsChange: (_value: number | undefined) => void
  onMinBathroomsChange: (_value: number | undefined) => void
  onMinFloorChange: (_value: number | undefined) => void
  onMaxFloorChange: (_value: number | undefined) => void
  onMinContractMonthsChange: (_value: number | undefined) => void
  onMaxContractMonthsChange: (_value: number | undefined) => void
  onMinAvailablePlacesChange: (_value: number | undefined) => void
  onAvailableFromChange: (_value: string | undefined) => void
  onAvailableToChange: (_value: string | undefined) => void
  onCityIdChange: (_value: number | undefined) => void
  onFeatureToggle: (_slug: string, _checked: boolean) => void
  onFeatureGroupChange?: (_groupSlugs: string[], _selectedSlug: string | null) => void
  onOrderByChange: (_value: OrderByValue) => void
  onFiltersApply: () => void
}

const SearchFilters = ({
  cityId,
  minPrice,
  maxPrice,
  minFloor,
  maxFloor,
  availableFrom,
  availableTo,
  selectedFeatures,
  orderBy,
  isFeaturesLoading,
  features,
  onCityIdChange,
  onMinPriceChange,
  onMaxPriceChange,
  onMinFloorChange,
  onMaxFloorChange,
  onAvailableFromChange,
  onAvailableToChange,
  onFeatureToggle,
  onFeatureGroupChange,
  onOrderByChange,
  onFiltersApply
}: SearchFiltersProps) => {
  // Prevent hydration mismatch for DatePicker
  const { t } = useTranslation('common')
  const { strings } = useAppStore()
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const { collection: cities } = useCities({ page: 0, per_page: 100, onlyWithAdvertisements: true })
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  const handleOrderByChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      onOrderByChange(event.target.value as OrderByValue)
    },
    [onOrderByChange]
  )

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 4,
        padding: (theme) => theme.spacing(2, 2, 3),
        bgcolor: (theme) => theme.palette.background.paper,
        borderRadius: (theme) => theme.shape.borderRadius4,
        boxShadow: (theme) => ({ xs: theme.shadows[2], md: 0 })
      }}
    >
      {/* Order by section */}
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography
          variant="textLarge"
          fontWeight={isHappa ? '700' : '500'}
          color="text.secondaryContrastText"
          sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
        >
          {strings?.labels?.order_by}
        </Typography>
        <FormControl size="small">
          <Select
            value={orderBy || '-date'}
            onChange={handleOrderByChange}
            variant="standard"
            displayEmpty
            sx={{
              borderRadius: (theme) => theme.shape.borderRadius2,
              '& .MuiSelect-select': {
                py: 1
              }
            }}
          >
            <MenuItem value="-date">{strings?.labels?.most_recent}</MenuItem>
            <MenuItem value="date">{strings?.labels?.least_recent}</MenuItem>
            <MenuItem value="price">{strings?.labels?.price_ascending}</MenuItem>
            <MenuItem value="-price">{strings?.labels?.price_descending}</MenuItem>
            <MenuItem value="available_from">{strings?.labels?.availability}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Typography
        variant="textLarge"
        fontWeight={isHappa ? '700' : '500'}
        color="text.secondaryContrastText"
        sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
      >
        {strings?.labels?.filter_by}
      </Typography>

      {/* Città (solo isHappa, nascosta se city_id è già impostato da query string) */}
      {isHappa && !cityId && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography
            variant="textRegular"
            fontWeight="500"
            color="text.secondaryContrastText"
            sx={{ fontFamily: 'var(--heading-font)' }}
          >
            {t('city.label')}
          </Typography>
          <FormControl size="small">
            <Select
              value={cityId?.toString() ?? ''}
              onChange={(e: SelectChangeEvent<string>) => {
                onCityIdChange(e.target.value ? parseInt(e.target.value, 10) : undefined)
              }}
              variant="standard"
              displayEmpty
              sx={{
                borderRadius: (theme) => theme.shape.borderRadius2,
                '& .MuiSelect-select': { py: 1 }
              }}
            >
              <MenuItem value="">{t('city.allCities')}</MenuItem>
              {(cities || []).map((city: City) => (
                <MenuItem key={city.id} value={city.id}>
                  {city.name?.[currentLocale || 'it'] || city.name?.it || city.name?.en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* --- Feature groups rendered inline by slug --- */}
      {(() => {
        const HIDDEN_FEATURE_SLUGS = [
          'private-bathroom',
          'mixed_room',
          'induction_cooktop',
          'induction-cooktop',
          'has_windows'
        ]

        const getFeatureGroup = (slug: string) => {
          if (isFeaturesLoading || !features) return null
          const group = features[slug]
          if (!group) return null
          const filteredGroup =
            slug === 'ungrouped'
              ? {
                  ...group,
                  features: group.features.filter((f) => !HIDDEN_FEATURE_SLUGS.includes(f.slug))
                }
              : group
          return (
            <FeatureFilterGroup
              key={slug}
              groupKey={slug}
              group={filteredGroup}
              selectedFeatures={selectedFeatures}
              onToggle={onFeatureToggle}
              onGroupChange={onFeatureGroupChange}
            />
          )
        }

        return (
          <>
            {/* 1. Tipo sistemazione */}
            {getFeatureGroup('accommodation_type')}

            {/* 2. Ospiti in stanza */}
            {getFeatureGroup('guests')}

            {/* 3. Canone mensile (min/max) */}
            <PriceRangeInput
              minPrice={minPrice}
              maxPrice={maxPrice}
              onMinChange={onMinPriceChange}
              onMaxChange={onMaxPriceChange}
            />

            {/* 4. Disponibile dal */}
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Typography
                variant="textRegular"
                fontWeight="500"
                color="text.secondaryContrastText"
                sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
              >
                {strings?.labels?.available_from}
              </Typography>
              {isMounted ? (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={currentLocale || 'it'}
                >
                  <DatePicker
                    value={availableFrom ? dayjs(availableFrom) : null}
                    onChange={(newValue) => {
                      onAvailableFromChange(newValue ? newValue.format('YYYY-MM-DD') : undefined)
                    }}
                    slots={{
                      openPickerIcon: () => <CalendarDaysIcon size={16} />
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        variant: 'standard',
                        sx: {
                          borderRadius: (theme) => theme.shape.borderRadius2,
                          '& .MuiInputBase-root': {
                            py: 1
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              ) : (
                <TextField size="small" variant="standard" disabled placeholder="Seleziona data" />
              )}
            </Box>

            {/* 5. Disponibile fino al */}
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Typography
                variant="textRegular"
                fontWeight="500"
                color="text.secondaryContrastText"
                sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
              >
                {strings?.labels?.available_until}
              </Typography>
              {isMounted ? (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={currentLocale || 'it'}
                >
                  <DatePicker
                    value={availableTo ? dayjs(availableTo) : null}
                    onChange={(newValue) => {
                      onAvailableToChange(newValue ? newValue.format('YYYY-MM-DD') : undefined)
                    }}
                    slots={{
                      openPickerIcon: () => <CalendarDaysIcon size={16} />
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        variant: 'standard',
                        sx: {
                          borderRadius: (theme) => theme.shape.borderRadius2,
                          '& .MuiInputBase-root': {
                            py: 1
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              ) : (
                <TextField size="small" variant="standard" disabled placeholder="Seleziona data" />
              )}
            </Box>

            {/* 6. Tipo immobile */}
            {getFeatureGroup('struct_type')}

            {/* 8. Piano */}
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Typography
                variant="textRegular"
                fontWeight="500"
                color="text.secondaryContrastText"
                sx={{ fontFamily: isHappa ? 'var(--heading-font)' : 'var(--base-font)' }}
              >
                {strings?.labels?.floor}
              </Typography>
              <Stack direction="row" spacing={4} alignItems="center">
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={minFloor?.toString() ?? ''}
                    onChange={(event) => {
                      const value = event.target.value
                        ? parseInt(event.target.value, 10)
                        : undefined
                      onMinFloorChange(value)
                    }}
                    variant="standard"
                    displayEmpty
                    sx={{
                      borderRadius: (theme) => theme.shape.borderRadius2,
                      '& .MuiSelect-select': { py: 1 }
                    }}
                  >
                    <MenuItem value="">{strings?.labels?.any}</MenuItem>
                    <MenuItem value="0">{strings?.labels?.ground_floor}</MenuItem>
                    <MenuItem value="1">1°</MenuItem>
                    <MenuItem value="2">2°</MenuItem>
                    <MenuItem value="3">3°</MenuItem>
                    <MenuItem value="4">4°</MenuItem>
                    <MenuItem value="5">5°+</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <Select
                    value={maxFloor?.toString() ?? ''}
                    onChange={(event) => {
                      const value = event.target.value
                        ? parseInt(event.target.value, 10)
                        : undefined
                      onMaxFloorChange(value)
                    }}
                    variant="standard"
                    displayEmpty
                    sx={{
                      borderRadius: (theme) => theme.shape.borderRadius2,
                      '& .MuiSelect-select': { py: 1 }
                    }}
                  >
                    <MenuItem value="">{strings?.labels?.any}</MenuItem>
                    <MenuItem value="0">{strings?.labels?.ground_floor}</MenuItem>
                    <MenuItem value="1">1°</MenuItem>
                    <MenuItem value="2">2°</MenuItem>
                    <MenuItem value="3">3°</MenuItem>
                    <MenuItem value="4">4°</MenuItem>
                    <MenuItem value="5">5°+</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            {/* 9. Caratteristiche */}
            {getFeatureGroup('ungrouped')}

            {/* 10. Distanza trasporti pubblici */}
            {getFeatureGroup('distance_transp')}

            {/* 11. Proprietario residente */}
            {getFeatureGroup('owner_resident')}
          </>
        )
      })()}

      <Box sx={{ display: { md: 'none' } }}>
        <Button variant="contained" color="primary" fullWidth onClick={onFiltersApply}>
          {strings?.labels?.apply_filter}
        </Button>
      </Box>
    </Box>
  )
}

/**
 * Component: SearchFiltersPanel
 */
type SearchFiltersPanelProps = GridProps

const SearchFiltersPanel = ({ sx, ...props }: SearchFiltersPanelProps) => {
  const {
    filters,
    setQ,
    setCityId,
    setMinPrice,
    setMaxPrice,
    setMinDeposit,
    setMaxDeposit,
    setMinArea,
    setMaxArea,
    setMinBedrooms,
    setMinBathrooms,
    setMinFloor,
    setMaxFloor,
    setMinContractMonths,
    setMaxContractMonths,
    setMinAvailablePlaces,
    setAvailableFrom,
    setAvailableTo,
    toggleFeature,
    setFeatureGroup,
    setOrderBy
  } = useSearchFilters()
  const { features, isLoading: isFeaturesLoading } = useFeatures()
  const [filtersOpen, setLayoutState] = useLayoutStore(
    useShallow((state) => [state.filtersOpen, state.setState])
  )

  return (
    <Grid
      sx={{
        position: { xs: 'fixed', md: 'static' },
        top: 0,
        left: 0,
        transform: { xs: `translateY(${filtersOpen ? 0 : 100}%)`, md: 'none' },
        transition: (theme) => `transform 0.7s ${theme.customEasings.outCubic}`,
        borderRadius: (theme) => ({ md: theme.shape.borderRadius4 }),
        boxShadow: (theme) => theme.shadows[2],
        bgcolor: (theme) => theme.palette.grey[50],
        height: '100%',
        zIndex: { xs: 101, md: 1 },
        opacity: 1,
        ...sx
      }}
      {...props}
    >
      <IconButton
        size="medium"
        onClick={() => setLayoutState({ filtersOpen: false })}
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 1301,
          bgcolor: 'background.paper',
          boxShadow: 1,
          '&:hover': {
            bgcolor: 'background.paper'
          }
        }}
      >
        <XIcon size={20} />
      </IconButton>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          px: { xs: 2, md: 0 },
          py: { xs: 4, md: 0 }
        }}
      >
        <SearchFilters
          q={filters.q}
          cityId={filters.city_id}
          minPrice={filters.min_price}
          maxPrice={filters.max_price}
          minDeposit={filters.min_deposit}
          maxDeposit={filters.max_deposit}
          minArea={filters.min_area}
          maxArea={filters.max_area}
          minBedrooms={filters.min_bedrooms}
          minBathrooms={filters.min_bathrooms}
          minFloor={filters.min_floor}
          maxFloor={filters.max_floor}
          minContractMonths={filters.min_contract_months}
          maxContractMonths={filters.max_contract_months}
          minAvailablePlaces={filters.min_available_places}
          availableFrom={filters.available_from}
          availableTo={filters.available_to}
          selectedFeatures={filters.features}
          orderBy={filters.order_by}
          isFeaturesLoading={isFeaturesLoading}
          features={features}
          onQChange={setQ}
          onCityIdChange={setCityId}
          onMinPriceChange={setMinPrice}
          onMaxPriceChange={setMaxPrice}
          onMinDepositChange={setMinDeposit}
          onMaxDepositChange={setMaxDeposit}
          onMinAreaChange={setMinArea}
          onMaxAreaChange={setMaxArea}
          onMinBedroomsChange={setMinBedrooms}
          onMinBathroomsChange={setMinBathrooms}
          onMinFloorChange={setMinFloor}
          onMaxFloorChange={setMaxFloor}
          onMinContractMonthsChange={setMinContractMonths}
          onMaxContractMonthsChange={setMaxContractMonths}
          onMinAvailablePlacesChange={setMinAvailablePlaces}
          onAvailableFromChange={setAvailableFrom}
          onAvailableToChange={setAvailableTo}
          onFeatureToggle={toggleFeature}
          onFeatureGroupChange={setFeatureGroup}
          onOrderByChange={setOrderBy}
          onFiltersApply={() => setLayoutState({ filtersOpen: false })}
        />
      </Box>
    </Grid>
  )
}

const SCROLL_STORAGE_KEY = 'search-scroll-y'

/**
 * Main Component: Search
 */
const Search = ({ pageProps }: SearchProps) => {
  const lenis = useLenis()
  const router = useRouter()
  const acf = useMemo(() => pageProps?.acf, [pageProps])
  const titleRendered = useMemo(() => pageProps?.title, [pageProps])
  const contentRendered = useMemo(() => pageProps?.content?.rendered, [pageProps])
  const { filters, searchParams, setPage } = useSearchFilters()
  const {
    collection,
    pagination,
    isLoading: isSearchLoading
  } = useAdvertisementsSearch(searchParams)

  const setLayoutState = useLayoutStore((state) => state.setState)
  const [mapModalOpen, setMapModalOpen] = useState(false)
  const pendingScrollRef = useRef<number | null>(null)

  // Save scroll position before navigating away
  useEffect(() => {
    const handleRouteChange = () => {
      if (lenis) {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, String(Math.round(lenis.scroll)))
      }
    }

    router.events.on('routeChangeStart', handleRouteChange)
    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events, lenis])

  // Read saved scroll position once on mount
  useEffect(() => {
    const savedY = sessionStorage.getItem(SCROLL_STORAGE_KEY)
    if (savedY) {
      sessionStorage.removeItem(SCROLL_STORAGE_KEY)
      const y = parseInt(savedY, 10)
      if (!isNaN(y) && y > 0) {
        pendingScrollRef.current = y
      }
    }
  }, [])

  // Restore scroll position once data is loaded and lenis is ready
  useEffect(() => {
    if (pendingScrollRef.current === null) return
    if (isSearchLoading || !collection || !lenis) return

    const y = pendingScrollRef.current
    pendingScrollRef.current = null

    requestAnimationFrame(() => {
      lenis.scrollTo(y, { immediate: true })
    })
  }, [isSearchLoading, collection, lenis])

  // Convert searchParams to AdvertisementSearchParams for the map modal
  const mapInitialFilters = useMemo<Partial<AdvertisementSearchParams>>(
    () => ({
      city_id: filters.city_id,
      min_price: filters.min_price,
      max_price: filters.max_price,
      features: filters.features.length > 0 ? filters.features : undefined
    }),
    [filters.city_id, filters.min_price, filters.max_price, filters.features]
  )

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, page: number) => {
      setPage(page)
      lenis?.scrollTo(0, { immediate: true })
    },
    [setPage, lenis]
  )

  return (
    <>
      <Container>
        <Grid container alignItems="start">
          <Grid size={{ xs: 4, sm: 8, md: 12 }}>
            <SearchHeader
              title={titleRendered?.rendered || acf?.title || 'Risultati di ricerca...'}
              onOpenFilters={() => setLayoutState({ filtersOpen: true })}
              onOpenMap={() => setMapModalOpen(true)}
            />
          </Grid>
          <Grid size={{ xs: 4, sm: 8, md: 6 }}>
            {contentRendered ? (
              <Typography
                variant="textRegular"
                color="text.secondary"
                component="div"
                sx={{ mt: 2 }}
                dangerouslySetInnerHTML={{ __html: contentRendered }}
              />
            ) : null}
          </Grid>

          <SearchFiltersPanel size={{ xs: 4, sm: 8, md: 4, lg: 3 }} order={{ xs: 0, md: 1 }} />

          <Grid
            container
            size={{ xs: 4, sm: 8, md: 8, lg: 9 }}
            columns={{ xs: 4, sm: 8, md: 8, lg: 9 }}
          >
            <SearchResults isLoading={isSearchLoading} collection={collection} />
          </Grid>
        </Grid>
      </Container>

      <Spacer size="2xl" />

      {pagination && pagination.last_page > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <SearchPagination
            count={pagination.last_page}
            page={filters.page}
            onChange={handlePageChange}
          />
        </Box>
      )}

      <Spacer size="2xl" />

      {/* Map Search Modal */}
      <MapSearchModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        initialFilters={mapInitialFilters}
      />
    </>
  )
}

export default Search
